/**
 * Cross-subdomain sign-in between map.proxi.co (legacy Lit client) and
 * app.proxi.co (Next.js).
 *
 * Both apps store auth in localStorage as `accountInformation`. The server
 * keeps an HttpOnly `proxi_session` cookie on `.proxi.co` so a sign-in on one
 * subdomain can be fetched on the other; no page script can read it. The only
 * readable cross-site cookie is `proxi_signed_in`, a bare flag the marketing
 * site uses to swap Log In for Dashboard. The server sets and clears both
 * cookies together, so the flag never outlives the session.
 */

import { post } from './api/api';
import { isDefined } from './utils';

const SIGNED_IN_COOKIE = 'proxi_signed_in';
/** Carried the secret in the clear before October 2026. Read once to migrate, then removed. */
const LEGACY_AUTH_COOKIE = 'proxi_auth';
const UI_PREF_COOKIE = 'proxi_ui_pref';
const ORG_COOKIE = 'proxi_org';
const COOKIE_DOMAIN = '.proxi.co';

const NEW_UI_CUTOFF = new Date('2026-03-10T00:00:00Z');

function isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function secureSuffix(): string {
    return isLocalhost() ? '' : '; Secure';
}

function domainSuffix(): string {
    return isLocalhost() ? '' : `; domain=${COOKIE_DOMAIN}`;
}

/** A custom domain can't hold a `.proxi.co` cookie, so there is no session to share. */
function canShareSession(): boolean {
    return isLocalhost() || window.location.hostname.endsWith(COOKIE_DOMAIN);
}

interface SessionInfo {
    email: string;
    device_name: string;
    secret: string;
    expires_at?: string;
}

function readCookie(name: string): string | undefined {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    return isDefined(match) ? match.slice(name.length + 1) : undefined;
}

function expireCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainSuffix()}; path=/${secureSuffix()}; SameSite=Lax`;
}

/**
 * Make a fresh sign-in visible beyond this origin: the server stores it in
 * the HttpOnly session cookie, and the readable flag tells the marketing site
 * someone is signed in. Call wherever localStorage gets new credentials.
 */
let pendingPersist: AbortController | undefined;

export async function persistSession(info: SessionInfo): Promise<void> {
    if (!canShareSession()) return;
    const authorization = `Basic ${btoa(`${info.email}:${info.device_name}:${info.secret}`)}`;
    pendingPersist?.abort();
    const controller = new AbortController();
    pendingPersist = controller;
    // keepalive: the migration path reloads right after this. A failed request is
    // retried on the next load, because the flag is only set with the cookie.
    try {
        await fetch('/api/account/session', { method: 'POST', headers: { Authorization: authorization }, keepalive: true, signal: controller.signal });
    } catch {
        // The sibling subdomain just asks for a login; this origin is already signed in.
    } finally {
        if (pendingPersist === controller) pendingPersist = undefined;
    }
}

/** Sign out everywhere a sign-in was shared. Only the server can clear the HttpOnly cookie. */
export function endSession(): void {
    pendingPersist?.abort();
    pendingPersist = undefined;
    expireCookie(SIGNED_IN_COOKIE);
    expireCookie(LEGACY_AUTH_COOKIE);
    clearOrgCookie();
    if (!canShareSession()) return;

    void fetch('/api/account/session', { method: 'DELETE', keepalive: true }).catch(() => {
        // Worst case the sibling subdomain re-checks a token the server no longer honors.
    });
}

function isSignedInElsewhere(): boolean {
    return readCookie(SIGNED_IN_COOKIE) === '1';
}

function readLegacyAuthCookie(): SessionInfo | null {
    const raw = readCookie(LEGACY_AUTH_COOKIE);
    if (!isDefined(raw)) return null;
    try {
        const info = JSON.parse(decodeURIComponent(raw)) as Partial<SessionInfo>;
        if (!isDefined(info.email) || !isDefined(info.device_name) || !isDefined(info.secret)) return null;
        if (isDefined(info.expires_at) && info.expires_at < new Date().toISOString()) return null;
        return info as SessionInfo;
    } catch {
        return null;
    }
}

function storeSession(info: SessionInfo): void {
    localStorage.setItem('accountInformation', JSON.stringify(info));
    localStorage.setItem('email', info.email);
}

/**
 * Pull a sign-in made on app.proxi.co into localStorage. Call once at boot;
 * resolves true when credentials were stored, so the caller can reload and
 * let every component start out signed in.
 */
export async function hydrateFromSession(): Promise<boolean> {
    // Org switches made on app.proxi.co should show here too, signed in or not.
    const orgId = getOrgCookie();
    if (isDefined(orgId)) {
        localStorage.setItem('organization_id', orgId);
    }

    const existing = localStorage.getItem('accountInformation');
    if (existing !== null) {
        // Signed in before the session cookie existed: give the sibling subdomain something to find.
        if (!isSignedInElsewhere()) {
            try {
                void persistSession(JSON.parse(existing) as SessionInfo);
            } catch {
                // Unreadable local state is cleared by getAccountInformation.
            }
        }
        expireCookie(LEGACY_AUTH_COOKIE);
        return false;
    }

    const legacy = readLegacyAuthCookie();
    expireCookie(LEGACY_AUTH_COOKIE);
    if (legacy !== null) {
        storeSession(legacy);
        void persistSession(legacy);
        return true;
    }

    if (!isSignedInElsewhere()) return false;

    try {
        const response = await fetch('/api/account/session');
        if (!response.ok) {
            expireCookie(SIGNED_IN_COOKIE);
            return false;
        }
        const info = (await response.json()) as SessionInfo;
        if (!isDefined(info?.email)) return false;
        storeSession(info);
        return true;
    } catch {
        return false;
    }
}

// ---------------------------------------------------------------------------
// Organization cookie — keeps selected org in sync across proxi.co domains
// ---------------------------------------------------------------------------

/** Write the selected organization ID to a cross-subdomain cookie. */
export function setOrgCookie(organizationId: string): void {
    const value = encodeURIComponent(organizationId);
    const d = new Date();
    d.setDate(d.getDate() + 30);
    const expires = `; expires=${d.toUTCString()}`;
    const domain = isLocalhost() ? '' : `; domain=${COOKIE_DOMAIN}`;
    document.cookie = `${ORG_COOKIE}=${value}${expires}${domain}; path=/${secureSuffix()}; SameSite=Lax`;
}

/** Read the selected organization ID from the cross-subdomain cookie. */
export function getOrgCookie(): string | null {
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${ORG_COOKIE}=`));
    if (!match) return null;
    try {
        return decodeURIComponent(match.split('=').slice(1).join('=')) || null;
    } catch {
        return null;
    }
}

/** Remove the org cookie (e.g. on logout). */
export function clearOrgCookie(): void {
    expireCookie(ORG_COOKIE);
}

// ---------------------------------------------------------------------------
// UI preference cookie — synced across map.proxi.co and app.proxi.co
// ---------------------------------------------------------------------------

export type UiPref = 'classic' | 'new';

export function setUiPref(pref: UiPref): void {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    document.cookie = `${UI_PREF_COOKIE}=${pref}; expires=${d.toUTCString()}${domainSuffix()}; path=/${secureSuffix()}; SameSite=Lax`;
}

export function getUiPref(): UiPref | null {
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${UI_PREF_COOKIE}=`));
    if (!match) return null;
    const val = match.split('=')[1];
    return val === 'classic' || val === 'new' ? val : null;
}

export function isLegacyAccount(objectIdHex: string): boolean {
    const timestamp = parseInt(objectIdHex.substring(0, 8), 16) * 1000;
    return new Date(timestamp) < NEW_UI_CUTOFF;
}

export function resolveUiForAccount(objectIdHex: string): UiPref {
    if (!isLegacyAccount(objectIdHex)) return 'new';
    return getUiPref() ?? 'classic';
}

/** Returns true if the current hostname is a beta environment. */
export function isBeta(): boolean {
    const host = window.location.hostname;
    return host === 'betamap.proxi.co' || host === 'betaapp.proxi.co';
}

/** Returns the base URL for the new React app (app.proxi.co or localhost:3000). */
export function getNewUiUrl(): string {
    if (isLocalhost()) return 'http://localhost:3000';
    if (isBeta()) return 'https://betaapp.proxi.co';
    return 'https://app.proxi.co';
}

/** Returns the base URL for the classic Lit app (map.proxi.co or localhost:8082). */
export function getClassicUiUrl(): string {
    if (isLocalhost()) return 'http://localhost:8082';
    if (isBeta()) return 'https://betamap.proxi.co';
    return 'https://map.proxi.co';
}

/** Returns true if the current hostname is a custom domain (not *.proxi.co or localhost). */
export function isCustomDomain(): boolean {
    const host = window.location.hostname;
    return !host.endsWith('.proxi.co') && !isLocalhost();
}

/**
 * Requests a one-time transfer token from the server, then redirects to the
 * new UI with the token in a URL fragment. Falls back to a plain redirect
 * if the token request fails (the user will just need to log in again).
 */
export async function redirectToNewUiWithTransfer(): Promise<void> {
    const newUiUrl = getNewUiUrl();
    try {
        const response = await fetch('/api/account/transfer-token', post({}));
        if (response.ok) {
            const data = await response.json();
            window.location.href = `${newUiUrl}#transfer_token=${encodeURIComponent(data.token)}`;
            return;
        }
    } catch {
        // Fall through to plain redirect
    }
    window.location.href = newUiUrl;
}

/**
 * Redirects to a specific path within the new UI, appending an auth
 * transfer token in the hash if the caller is authenticated. Used by
 * legacy edit links that opt into the new UI via `?new_experience=true`
 * — the recipient lands on the new edit page already authenticated
 * (when applicable), without losing their session crossing domains.
 *
 * `targetPath` should start with `/` (e.g. `/p/<id>/edit?point_key=...`).
 */
export async function redirectToNewUiPathWithTransfer(targetPath: string): Promise<void> {
    const newUiUrl = getNewUiUrl();
    const path = targetPath.startsWith('/') ? targetPath : `/${targetPath}`;
    try {
        const response = await fetch('/api/account/transfer-token', post({}));
        if (response.ok) {
            const data = await response.json();
            window.location.replace(
                `${newUiUrl}${path}#transfer_token=${encodeURIComponent(data.token)}`,
            );
            return;
        }
    } catch {
        // Fall through to plain redirect — recipient will need to
        // authenticate (or use a `point_key` already in the path).
    }
    window.location.replace(`${newUiUrl}${path}`);
}
